import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    IconButton,
    InputAdornment,
    Link as MuiLink,
    MenuItem,
    Paper,
    Snackbar,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import adminHelpService from '../services/AdminHelpService';
import { helpAnchorOptions, anchorLabel } from './helpAnchors';
import { LoomEmbed, LoomThumbnail, formatDuration } from './HelpContent';

const LANGUAGES = [
    { value: 'en', label: 'English' },
    { value: 'jp', label: '日本語' },
];

const PAGE_SIZE = 8;
const norm = (v) => String(v ?? '').trim();
const langLabel = (v) => LANGUAGES.find((l) => l.value === v)?.label || (v ? v : '—');

// Client-side mirror of the backend Loom id extractor (for the live preview).
function extractLoomId(value) {
    const raw = norm(value);
    if (!raw) return '';
    const m = raw.match(/loom\.com\/(?:share|embed)\/([A-Za-z0-9]+)/i);
    if (m) return m[1];
    const candidate = raw.split('?')[0].replace(/\/+$/, '').split('/').pop();
    return /^[A-Za-z0-9]{16,}$/.test(candidate) ? candidate : '';
}

// ── Loom preview card (right column of the modal) ───────────────────────────
function LoomPreviewCard({ loomId, title, duration }) {
    const { t } = useTranslation();
    return (
        <Box
            sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '12px',
                p: 2,
                bgcolor: 'background.paper',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                minHeight: 300,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', mr: 'auto' }}>
                    {t('adminHelpCenter.loomPreview', 'Loom Preview')}
                </Typography>
                {loomId ? (
                    <IconButton
                        size="small"
                        component={MuiLink}
                        href={`https://www.loom.com/share/${loomId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={t('adminHelpCenter.openInLoom', 'Open in Loom')}
                    >
                        <OpenInNewIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                ) : null}
            </Box>

            {loomId ? (
                <>
                    <LoomEmbed videoId={loomId} title={title} />
                    <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                            {title || t('adminHelpCenter.untitledVideo', 'Untitled video')}
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                            {[formatDuration(duration), t('adminHelpCenter.loomVideo', 'Loom Video')].filter(Boolean).join(' • ')}
                        </Typography>
                    </Box>
                    <Typography sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>
                        {t('adminHelpCenter.previewFetched', 'The preview is fetched from Loom content.')}
                    </Typography>
                </>
            ) : (
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        color: 'text.disabled',
                        border: '1px dashed',
                        borderColor: 'divider',
                        borderRadius: '10px',
                        p: 3,
                    }}
                >
                    <Typography sx={{ fontSize: '0.85rem' }}>
                        {t('adminHelpCenter.pastePrompt', 'Paste a Loom share URL to preview the video here.')}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

// ── Add / Edit dialog ───────────────────────────────────────────────────────
function ArticleDialog({ open, mode, initial, onClose, onSubmit, submitting }) {
    const { t } = useTranslation();
    const [title, setTitle] = useState('');
    const [summary, setSummary] = useState('');
    const [page, setPage] = useState('');
    const [loomUrl, setLoomUrl] = useState('');
    const [language, setLanguage] = useState('en');
    const [order, setOrder] = useState('1');
    const [status, setStatus] = useState('draft');
    const [touched, setTouched] = useState(false);

    useEffect(() => {
        if (!open) return;
        const a = initial || {};
        setTitle(norm(a.title));
        setSummary(norm(a.summary));
        setPage(Array.isArray(a.anchorKeys) && a.anchorKeys.length ? a.anchorKeys[0] : '');
        setLoomUrl(norm(a.loomVideoId));
        setLanguage(norm(a.language) || 'en');
        setOrder(a.order != null ? String(a.order) : '1');
        setStatus(norm(a.status) || 'draft');
        setTouched(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initial?.articleId]);

    const loomId = useMemo(() => extractLoomId(loomUrl), [loomUrl]);
    const loomInvalid = norm(loomUrl) !== '' && !loomId;
    const titleMissing = norm(title) === '';

    const canSubmit = !titleMissing && !!loomId;

    const handleSubmit = async () => {
        setTouched(true);
        if (!canSubmit) return;
        await onSubmit({
            title: norm(title),
            summary: norm(summary),
            loomVideoId: loomId,
            language,
            anchorKeys: page ? [page] : [],
            status,
            order: order === '' ? undefined : Number(order),
        });
    };

    const labelSx = { fontSize: '0.8rem', fontWeight: 600, color: 'text.secondary', mb: 0.5 };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" PaperProps={{ sx: { borderRadius: '14px' } }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', mr: 'auto' }}>
                    {mode === 'edit' ? t('adminHelpCenter.editTutorial', 'Edit Tutorial Video') : t('adminHelpCenter.addTutorial', 'Add Tutorial Video')}
                </Typography>
                <IconButton aria-label={t('adminHelpCenter.close', 'Close')} onClick={onClose} size="small" disabled={submitting}>
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* Body: two columns */}
            <Box sx={{ display: 'flex', gap: 3, p: 3, flexDirection: { xs: 'column', md: 'row' } }}>
                {/* Left: form */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                    <Box>
                        <Typography sx={labelSx}>{t('adminHelpCenter.titleRequired', 'Title *')}</Typography>
                        <TextField
                            value={title} onChange={(e) => setTitle(e.target.value)} onBlur={() => setTouched(true)}
                            placeholder={t('adminHelpCenter.titlePlaceholder', 'e.g. How to Use the Dashboard')} fullWidth size="small" disabled={submitting}
                            error={touched && titleMissing}
                            helperText={touched && titleMissing ? t('adminHelpCenter.titleRequiredError', 'Title is required') : ' '}
                        />
                    </Box>

                    <Box>
                        <Typography sx={labelSx}>{t('adminHelpCenter.displayPage', 'Display Page')}</Typography>
                        <TextField select value={page} onChange={(e) => setPage(e.target.value)}
                            fullWidth size="small" disabled={submitting} displayEmpty
                            SelectProps={{ displayEmpty: true, renderValue: (v) => (v ? anchorLabel(v) : t('adminHelpCenter.selectPage', 'Select page')) }}>
                            <MenuItem value=""><em>{t('adminHelpCenter.noneLibraryOnly', 'None (library only)')}</em></MenuItem>
                            {helpAnchorOptions.map((o) => <MenuItem key={o.key} value={o.key}>{o.label}</MenuItem>)}
                        </TextField>
                    </Box>

                    <Box>
                        <Typography sx={labelSx}>{t('adminHelpCenter.loomUrlRequired', 'Loom URL *')}</Typography>
                        <TextField value={loomUrl} onChange={(e) => setLoomUrl(e.target.value)}
                            placeholder="https://www.loom.com/share/…" fullWidth size="small" disabled={submitting}
                            error={loomInvalid}
                            helperText={loomInvalid ? t('adminHelpCenter.loomIdError', 'Could not detect a Loom video ID.') : t('adminHelpCenter.loomUrlHelper', 'Paste the public Loom share URL.')} />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={labelSx}>{t('adminHelpCenter.languageRequired', 'Language *')}</Typography>
                            <TextField select value={language} onChange={(e) => setLanguage(e.target.value)}
                                fullWidth size="small" disabled={submitting}>
                                {LANGUAGES.map((l) => <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>)}
                            </TextField>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={labelSx}>{t('adminHelpCenter.displayOrderRequired', 'Display Order *')}</Typography>
                            <TextField type="number" value={order} onChange={(e) => setOrder(e.target.value)}
                                fullWidth size="small" disabled={submitting} />
                        </Box>
                    </Box>

                    <Box>
                        <Typography sx={labelSx}>{t('adminHelpCenter.description', 'Description')}</Typography>
                        <TextField value={summary}
                            onChange={(e) => setSummary(e.target.value.slice(0, 300))}
                            placeholder={t('adminHelpCenter.descriptionPlaceholder', 'Briefly describe the content of this video (optional)')}
                            fullWidth size="small" multiline minRows={2} disabled={submitting}
                            helperText={`${summary.length}/300`} FormHelperTextProps={{ sx: { textAlign: 'right', mr: 0 } }} />
                    </Box>


                    <Box>
                        <Typography sx={labelSx}>{t('adminHelpCenter.status', 'Status')}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Switch checked={status === 'published'}
                                onChange={(e) => setStatus(e.target.checked ? 'published' : 'draft')}
                                disabled={submitting} color="success" />
                            <Typography sx={{ fontSize: '0.88rem' }}>
                                {status === 'published' ? t('adminHelpCenter.publishedLabel', 'Published (visible to customers)') : t('adminHelpCenter.draftLabel', 'Draft (hidden)')}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Right: preview */}
                <Box sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0 }}>
                    <LoomPreviewCard loomId={loomId} title={title} duration={initial?.duration} />
                </Box>
            </Box>

            {/* Footer */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Button onClick={onClose} disabled={submitting} variant="outlined" sx={{ borderRadius: '8px' }}>{t('adminHelpCenter.cancel', 'Cancel')}</Button>
                <Button onClick={handleSubmit} disabled={!canSubmit || submitting} variant="contained"
                    sx={{ borderRadius: '8px', px: 3, boxShadow: '0 2px 10px rgba(40,123,214,0.35)' }}>
                    {t('adminHelpCenter.save', 'Save')}
                </Button>
            </Box>
        </Dialog>
    );
}

// ── Main ────────────────────────────────────────────────────────────────────
export default function AdminHelpCenter() {
    const { t } = useTranslation();
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState('create');
    const [editing, setEditing] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const [query, setQuery] = useState('');
    const [pageFilter, setPageFilter] = useState('');
    const [langFilter, setLangFilter] = useState('');
    const [pageIndex, setPageIndex] = useState(0);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await adminHelpService.getArticles();
            setArticles(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error('Failed to load help articles', e);
            setSnackbar({ open: true, message: t('adminHelpCenter.loadFailed', 'Failed to load tutorials'), severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        const list = (Array.isArray(articles) ? [...articles] : []).filter((a) => {
            if (pageFilter && !(a.anchorKeys || []).includes(pageFilter)) return false;
            if (langFilter && norm(a.language) !== langFilter) return false;
            if (q) {
                const hay = [a.title, a.summary].map((v) => String(v ?? '').toLowerCase()).join(' ');
                if (!hay.includes(q)) return false;
            }
            return true;
        });
        list.sort((a, b) => (a.order || 0) - (b.order || 0) || norm(a.title).localeCompare(norm(b.title)));
        return list;
    }, [articles, query, pageFilter, langFilter]);

    const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const currentPage = Math.min(pageIndex, pageCount - 1);
    const paged = filtered.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE);

    useEffect(() => { setPageIndex(0); }, [query, pageFilter, langFilter]);

    const openCreate = () => { setDialogMode('create'); setEditing(null); setDialogOpen(true); };
    const openEdit = (a) => { setDialogMode('edit'); setEditing(a); setDialogOpen(true); };
    const closeDialog = () => { if (!saving) setDialogOpen(false); };

    const handleSubmit = async (payload) => {
        setSaving(true);
        try {
            if (dialogMode === 'edit') {
                await adminHelpService.updateArticle(editing.articleId, { ...editing, ...payload, articleId: editing.articleId });
                setSnackbar({ open: true, message: t('adminHelpCenter.tutorialUpdated', 'Tutorial updated'), severity: 'success' });
            } else {
                await adminHelpService.createArticle(payload);
                setSnackbar({ open: true, message: t('adminHelpCenter.tutorialCreated', 'Tutorial created'), severity: 'success' });
            }
            setDialogOpen(false);
            await load();
        } catch (e) {
            console.error('Failed to save tutorial', e);
            setSnackbar({ open: true, message: t('adminHelpCenter.saveFailed', 'Failed to save tutorial'), severity: 'error' });
            throw e;
        } finally {
            setSaving(false);
        }
    };

    const toggleStatus = async (a) => {
        const next = a.status === 'published' ? 'draft' : 'published';
        // Optimistic update.
        setArticles((prev) => prev.map((x) => (x.articleId === a.articleId ? { ...x, status: next } : x)));
        try {
            await adminHelpService.updateArticle(a.articleId, { ...a, status: next });
        } catch (e) {
            console.error('Failed to toggle status', e);
            setArticles((prev) => prev.map((x) => (x.articleId === a.articleId ? { ...x, status: a.status } : x)));
            setSnackbar({ open: true, message: t('adminHelpCenter.statusUpdateFailed', 'Failed to update status'), severity: 'error' });
        }
    };

    const handleDelete = async (a) => {
        if (!a?.articleId) return;
        if (!window.confirm(t('adminHelpCenter.deleteConfirm', 'Delete "{{title}}"?', { title: norm(a.title) || a.articleId }))) return;
        setSaving(true);
        try {
            await adminHelpService.deleteArticle(a.articleId);
            setSnackbar({ open: true, message: t('adminHelpCenter.tutorialDeleted', 'Tutorial deleted'), severity: 'success' });
            await load();
        } catch (e) {
            console.error('Failed to delete tutorial', e);
            setSnackbar({ open: true, message: t('adminHelpCenter.deleteFailed', 'Failed to delete tutorial'), severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const headSx = { fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'text.secondary', py: 1.5, px: 2, borderBottom: '1px solid', borderColor: 'divider', whiteSpace: 'nowrap' };
    const cellSx = { py: 1.5, px: 2, borderBottom: '1px solid', borderColor: 'divider', fontSize: '0.88rem' };

    const showingFrom = filtered.length === 0 ? 0 : currentPage * PAGE_SIZE + 1;
    const showingTo = Math.min(filtered.length, currentPage * PAGE_SIZE + PAGE_SIZE);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Header + toolbar */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ mr: 'auto' }}>
                    <Typography sx={{ fontSize: '1.6rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
                        {t('sideBar.helpCenterManage', 'Help Center Management')}
                    </Typography>
                    <Typography sx={{ fontSize: '0.9rem', color: 'text.secondary', mt: 0.25 }}>
                        {t('adminHelpCenter.subtitle', 'Register Loom videos and display them as usage guides for each page.')}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                    <TextField
                        value={query} onChange={(e) => setQuery(e.target.value)}
                        placeholder={t('adminHelpCenter.searchPlaceholder', 'Search videos…')} size="small"
                        InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>) }}
                        sx={{ width: 220, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                    />
                    <TextField select size="small" value={pageFilter} onChange={(e) => setPageFilter(e.target.value)}
                        SelectProps={{ displayEmpty: true, renderValue: (v) => (v ? anchorLabel(v) : t('adminHelpCenter.allPages', 'All Pages')) }}
                        sx={{ minWidth: 150, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}>
                        <MenuItem value="">{t('adminHelpCenter.allPages', 'All Pages')}</MenuItem>
                        {helpAnchorOptions.map((o) => <MenuItem key={o.key} value={o.key}>{o.label}</MenuItem>)}
                    </TextField>
                    <TextField select size="small" value={langFilter} onChange={(e) => setLangFilter(e.target.value)}
                        SelectProps={{ displayEmpty: true, renderValue: (v) => (v ? langLabel(v) : t('adminHelpCenter.allLanguages', 'All Languages')) }}
                        sx={{ minWidth: 150, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}>
                        <MenuItem value="">{t('adminHelpCenter.allLanguages', 'All Languages')}</MenuItem>
                        {LANGUAGES.map((l) => <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>)}
                    </TextField>
                    <Button variant="contained" onClick={openCreate} disabled={loading || saving} startIcon={<AddIcon />}
                        sx={{ borderRadius: '10px', fontWeight: 600, px: 2.5, boxShadow: '0 2px 10px rgba(40,123,214,0.35)' }}>
                        {t('adminHelpCenter.addVideo', 'Add Video')}
                    </Button>
                </Box>
            </Box>

            {/* Info banner */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, borderRadius: '10px', bgcolor: 'rgba(40,123,214,0.08)', border: '1px solid', borderColor: 'rgba(40,123,214,0.2)' }}>
                <InfoOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                    {t('adminHelpCenter.infoBanner', 'These will appear in the Help Center of the customer control panel.')}
                </Typography>
            </Box>

            {loading ? <Alert severity="info">{t('adminHelpCenter.loadingTutorials', 'Loading tutorials…')}</Alert> : null}

            {/* Table */}
            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '12px', overflow: 'hidden' }}>
                <Box sx={{ overflowX: 'auto' }}>
                    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
                        <Box component="thead">
                            <Box component="tr">
                                <Box component="th" sx={{ ...headSx, textAlign: 'left' }}>{t('adminHelpCenter.thumbnail', 'Thumbnail')}</Box>
                                <Box component="th" sx={{ ...headSx, textAlign: 'left' }}>{t('adminHelpCenter.titleHeader', 'Title')}</Box>
                                <Box component="th" sx={{ ...headSx, textAlign: 'left' }}>{t('adminHelpCenter.page', 'Page')}</Box>
                                <Box component="th" sx={{ ...headSx, textAlign: 'left' }}>{t('adminHelpCenter.language', 'Language')}</Box>
                                <Box component="th" sx={{ ...headSx, textAlign: 'left' }}>{t('adminHelpCenter.statusHeader', 'Status')}</Box>
                                <Box component="th" sx={{ ...headSx, textAlign: 'left' }}>{t('adminHelpCenter.order', 'Order')}</Box>
                                <Box component="th" sx={{ ...headSx, textAlign: 'right' }}>{t('adminHelpCenter.actions', 'Actions')}</Box>
                            </Box>
                        </Box>
                        <Box component="tbody">
                            {!loading && paged.length === 0 ? (
                                <Box component="tr">
                                    <Box component="td" colSpan={7} sx={{ ...cellSx, textAlign: 'center', color: 'text.secondary', py: 5 }}>
                                        {articles.length === 0 ? t('adminHelpCenter.emptyNoTutorials', 'No tutorials yet. Click “Add Video” to create one.') : t('adminHelpCenter.emptyNoMatch', 'No tutorials match your filters.')}
                                    </Box>
                                </Box>
                            ) : null}
                            {paged.map((a) => (
                                <Box component="tr" key={a.articleId}
                                    sx={{ transition: 'background-color 0.12s ease', '&:hover': { bgcolor: 'action.hover' } }}>
                                    <Box component="td" sx={cellSx}>
                                        <LoomThumbnail thumbnailUrl={a.thumbnailUrl} duration={a.duration} onClick={() => openEdit(a)} />
                                    </Box>
                                    <Box component="td" sx={{ ...cellSx, fontWeight: 600, color: 'text.primary', maxWidth: 260 }}>{a.title || '-'}</Box>
                                    <Box component="td" sx={{ ...cellSx, color: 'text.secondary' }}>
                                        {(a.anchorKeys || []).length ? anchorLabel(a.anchorKeys[0]) : '—'}
                                    </Box>
                                    <Box component="td" sx={{ ...cellSx, color: 'text.secondary' }}>{langLabel(a.language)}</Box>
                                    <Box component="td" sx={cellSx}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <Switch size="small" color="success" checked={a.status === 'published'}
                                                onChange={() => toggleStatus(a)} disabled={saving}
                                                inputProps={{ 'aria-label': t('adminHelpCenter.togglePublish', 'Toggle publish for {{title}}', { title: a.title }) }} />
                                            <Typography sx={{ fontSize: '0.82rem', color: a.status === 'published' ? 'success.main' : 'text.disabled' }}>
                                                {a.status === 'published' ? t('adminHelpCenter.published', 'Published') : t('adminHelpCenter.draft', 'Draft')}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box component="td" sx={{ ...cellSx, color: 'text.secondary' }}>{a.order ?? '—'}</Box>
                                    <Box component="td" sx={{ ...cellSx, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                        <Tooltip title={t('adminHelpCenter.edit', 'Edit')}><IconButton size="small" onClick={() => openEdit(a)} disabled={saving}
                                            sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}><EditOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                                        <Tooltip title={t('adminHelpCenter.delete', 'Delete')}><IconButton size="small" onClick={() => handleDelete(a)} disabled={saving}
                                            sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Paper>

            {/* Footer: count + pagination */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
                    {t('adminHelpCenter.showing', 'Showing {{from}}-{{to}} of {{total}} items', { from: showingFrom, to: showingTo, total: filtered.length })}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <IconButton size="small" disabled={currentPage === 0} onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px' }}><ChevronLeftIcon fontSize="small" /></IconButton>
                    {Array.from({ length: pageCount }, (_, i) => (
                        <Button key={i} size="small" onClick={() => setPageIndex(i)} variant={i === currentPage ? 'contained' : 'text'}
                            sx={{ minWidth: 34, borderRadius: '8px', ...(i !== currentPage && { color: 'text.secondary' }) }}>{i + 1}</Button>
                    ))}
                    <IconButton size="small" disabled={currentPage >= pageCount - 1} onClick={() => setPageIndex((p) => Math.min(pageCount - 1, p + 1))}
                        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px' }}><ChevronRightIcon fontSize="small" /></IconButton>
                </Box>
            </Box>

            <ArticleDialog open={dialogOpen} mode={dialogMode} initial={editing} onClose={closeDialog} onSubmit={handleSubmit} submitting={saving} />

            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
                <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
